import React, { useEffect } from "react";
import useDecibel from "../hooks/useDecibel";
import { Sonometer } from "sonometer/types/sonometer.model";
import { assetBaseUrl } from "./TextPopup";

interface LiveSoundDisplayProps {
  sonometer?: Sonometer;
}

const LiveSoundDisplay: React.FC<LiveSoundDisplayProps> = ({ sonometer }) => {
  const { decibelValue, isConnected } = useDecibel();
  const [maxRecord, setMaxRecord] = React.useState<number>(0);

  useEffect(() => {
    let currentMaxRecord = Number(
      localStorage.getItem(`championshipMaxScore-${sonometer?.championshipId}`)
    );
    if (currentMaxRecord && currentMaxRecord < 82.35) {
      currentMaxRecord = 82.35;
    }
    setMaxRecord(currentMaxRecord);
  }, [setMaxRecord, sonometer?.championshipId]);

  // Get the maximum ranked sonometer score (default to 101.6 as in reference)
  // const maxRecord = 93.6; //add maximum sonometer score here from server

  // Calculate marker position based on max record (same logic as reference)
  const calculateMarkerPosition = (maxRecord: number): number => {
    return Math.round((maxRecord - 90) * 30.6 + 630);
  };

  // Check if marker needs rotation and top positioning
  const needsRotation = (maxRecord: number): boolean => {
    return (
      maxRecord < 91.5 ||
      (maxRecord >= 93 && maxRecord <= 95) ||
      (maxRecord >= 96.8 && maxRecord <= 99.4) ||
      (maxRecord >= 101 && maxRecord <= 103)
    );
  };

  const calculateGaugeWidth = (value: number): number => {
    let percent: number;

    if (value < 90) {
      const lowRaw = (value / 90) * 46;
      percent = Math.max(25, lowRaw);
    } else if (value >= 105) {
      percent = 86.6;
    } else {
      percent = 2.7067 * value - 197.6;
    }

    return percent;
  };

  const gaugeWidth = calculateGaugeWidth(decibelValue);
  const markerPosition = calculateMarkerPosition(maxRecord) + 259.5;
  const shouldRotateMarker = needsRotation(maxRecord);

  console.log("max", maxRecord);

  return (
    <div
      className="w-[1540px] h-[220px] font-virage bg-cover bg-center relative"
      style={{
        backgroundImage: `url('${sonometer?.publicScreenSettings?.gaugeImage || "/images/sonometer_bg.png"}')`,
      }}
    >
      <div className="absolute top-2 right-2 flex items-center gap-2 bg-black bg-opacity-50 px-2 py-1 rounded">
        <div
          className={`w-2 h-2 rounded-full ${
            isConnected ? "bg-green-500 animate-pulse" : "#E30613"
          }`}
        ></div>
        <span className="text-whitetext-xs font-virage">
          {isConnected ? "LIVE" : "OFFLINE"}
        </span>
      </div>
      <div className="absolute top-0 left-0 h-full w-full">
        <img
          src={
            sonometer?.publicScreenSettings.sponsorLogo ||
            assetBaseUrl + "/images/sponsor_left.png"
          }
          alt="Sponsor"
          className="w-auto h-auto"
        />
      </div>

      <div className="flex items-center justify-start h-full relative">
        <div className="absolute left-[380px] w-[1130px] h-[52px] bg-white rounded-[26px] overflow-hidden">
          <div
            className="h-full bg-psg-blue rounded-l-[26px] transition-all duration-200 ease-out"
            style={{ width: `${gaugeWidth}%` }}
          ></div>
        </div>

        {/* Updated marker with dynamic positioning */}
        <div
          className={`absolute w-[19px] h-[104px] z-30 ${
            shouldRotateMarker ? "top-[71px]" : "top-[44%] -translate-y-1/2"
          }`}
          style={{
            left: `${markerPosition}px`,
            display: !maxRecord ? "none" : "block",
          }}
        >
          <img
            src={assetBaseUrl + "/images/best_score.png"}
            alt="Marker"
            className={`w-full h-full ${
              shouldRotateMarker ? "rotate-180" : ""
            }`}
          />
        </div>

        <div className="absolute left-[433px] w-[100px] text-white text-5xl font-bold font-virage tabular-nums pt-2 z-20 text-right">
          {decibelValue.toFixed(1)}
        </div>

        <div className="absolute left-[554px] text-white text-5xl font-bold font-virage pt-2 z-20">
          dB
        </div>
      </div>
    </div>
  );
};

export default LiveSoundDisplay;
